import MomentUtils from "@date-io/moment";
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import {
    KeyboardDateTimePicker,
    MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BiPlus } from "react-icons/bi";
import { BsFiletypeHtml } from "react-icons/bs";
import { FiTrash2 } from "react-icons/fi";
import { toastError } from "../../../utils";
import HtmlImportGuide from "../../../components/Common/HtmlImportGuide";
import { DownloadPdf } from "./downloadPdf";

const useStyles = makeStyles((theme) => ({
    tableContainer: {
        margin: theme.spacing(3),
        maxWidth: "100%",
        overflowX: "auto",
    },
    tableHeader: {
        backgroundColor: theme.palette.primary.main,
        "& th": {
            color: theme.palette.common.white,
            fontWeight: "bold",
        },
    },
    datePicker: {
        "& .MuiInputBase-root": {
            padding: "0 2px",
            border: "none",
            borderRadius: "0",
            backgroundColor: "transparent",
            fontSize: "11px",
            height: "24px",
        },
        "& .MuiInputBase-input": {
            padding: 0,
            fontSize: "11px",
        },
    },
    actionCell: {
        width: "120px",
    },
    addButton: {
        margin: theme.spacing(3),
        display: "flex",
        justifyContent: "flex-end",
    },
}));

const calculateTimeRows = (rawRows) => {
    const updatedRows = rawRows.map((row, i, arr) => {
        const prevToTime = i > 0 ? arr[i - 1].toTime : null;

        const fromTimeRaw = row.fromTime || prevToTime;
        const toTimeRaw = row.toTime;

        const percent = Number(row.percentTimeToCount) || 0;

        const fromTime =
            fromTimeRaw && moment(fromTimeRaw).isValid() ? moment(fromTimeRaw) : null;
        const toTime =
            toTimeRaw && moment(toTimeRaw).isValid() ? moment(toTimeRaw) : null;

        let fromDay = "",
            toDay = "",
            timeDuration = 0,
            timeUsed = 0;

        if (fromTime) {
            fromDay = fromTime.format("ddd");
        }

        if (toTime) {
            toDay = toTime.format("ddd");
        }

        if (fromTime && toTime) {
            if (toTime.isBefore(fromTime)) {
                toastError("End date cannot be before start date");
                return row;
            } else {
                timeDuration = +toTime.diff(fromTime, "days", true).toFixed(3);
                timeUsed = +(timeDuration * (percent / 100)).toFixed(3);
            }
        }

        return {
            ...row,
            fromTime: fromTime ? fromTime.toISOString() : "",
            fromDay,
            toTime: toTime ? toTime.toISOString() : "",
            toDay,
            timeDuration,
            timeUsed,
        };
    });

    let cumulative = 0;
    return updatedRows.map((row) => {
        cumulative += row.timeUsed || 0;
        return {
            ...row,
            cumulatedTimeUsed: +cumulative.toFixed(3),
        };
    });
};

const calculateFormData = (cargoQty, dischargeRate) => {
    const qty = parseFloat(cargoQty) || 0;
    const rate = parseFloat(dischargeRate) || 1;
    return (qty / rate).toFixed(3);
};

const calculateSummaryDetails = (rows, laytimeStatementDetails) => {
    let lastNotCounted = 0;
    let lastDemurrageBeginTime = null;
    let demurrageStartSet = false;
    let lastDemurrageBegin = null;
    const totalTimeAllowedDays = parseFloat(
        laytimeStatementDetails.totalTimeAllowed || 0
    );

    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const { timeDuration, timeUsed, fromTime, toTime } = row;

        let diff = 0;
        if (
            timeDuration !== undefined &&
            timeUsed !== undefined &&
            timeDuration !== null &&
            timeUsed !== null
        ) {
            diff = Number(timeDuration) - Number(timeUsed);
        }
        lastNotCounted = Number(diff.toFixed(3));

        if (fromTime && toTime) {
            let demurrageBegin;

            if (!demurrageStartSet) {
                const baseDate = new Date(toTime);
                const msToAdd = totalTimeAllowedDays * 24 * 60 * 60 * 1000;
                demurrageBegin = new Date(baseDate.getTime() + msToAdd);
                demurrageStartSet = true;
            } else {
                const prevDemurrageDate = new Date(lastDemurrageBegin);
                const msToAdd = lastNotCounted * 24 * 60 * 60 * 1000;
                demurrageBegin = new Date(prevDemurrageDate.getTime() + msToAdd);
            }
            lastDemurrageBegin = demurrageBegin.toISOString();
            lastDemurrageBeginTime = lastDemurrageBegin;
        }
    }
    return {
        vesselOnDemurrage: lastDemurrageBeginTime,
        lastNotCounted,
        lastDemurrageBeginTime,
    };
};

const LaytimeModel = ({ state, setState, onDownloadElectricPdf }) => {
    const classes = useStyles();
    const inputFileRef = useRef(null);
    const [openUploadHTML, setOpenUploadHTML] = useState(false);

    const onButtonClick = () => {
        setOpenUploadHTML(true);
    };

    const laytimeData = state?.data || {};

    const summaryDetails = useMemo(() => {
        return calculateSummaryDetails(
            laytimeData?.eventTable || [],
            laytimeData || {}
        );
    }, [laytimeData]);

    useEffect(() => {
        if (summaryDetails.vesselOnDemurrage) {
            setState((prev) => ({
                ...prev,
                data: {
                    ...prev.data,
                    vesselOnDemurrage: summaryDetails.vesselOnDemurrage,
                },
            }));
        }
    }, [summaryDetails.vesselOnDemurrage, setState]);

    const laytimeSummary = useMemo(() => {
        const totalDuration = laytimeData.eventTable?.reduce(
            (sum, row) => sum + (parseFloat(row.timeDuration) || 0),
            0
        );
        const totalTimeUsed = laytimeData.eventTable?.reduce(
            (sum, row) => sum + (parseFloat(row.timeUsed) || 0),
            0
        );
        const totalTimeAllowed = parseFloat(laytimeData.totalTimeAllowed) || 0;
        const difference = totalTimeUsed - totalTimeAllowed;
        const demurrage =
            difference > 0
                ? difference * (parseFloat(laytimeData.despatchRate) || 0)
                : difference * (parseFloat(laytimeData.demurrageRate) || 0);

        return {
            totalDuration,
            totalTimeUsed,
            totalTimeAllowed,
            difference,
            demurrage,
        };
    }, [
        laytimeData.eventTable,
        laytimeData.totalTimeAllowed,
        laytimeData.despatchRate,
        laytimeData.demurrageRate,
    ]);

    const updateLayTimeData = useCallback(
        (field, value) => {
            setState((prevState) => ({
                ...prevState,
                data: {
                    ...prevState.data,
                    [field]: value,
                    ...(field === "cargoQty" || field === "dischargingRate"
                        ? {
                            totalTimeAllowed: calculateFormData(
                                field === "cargoQty" ? value : laytimeData.cargoQty,
                                field === "dischargingRate"
                                    ? value
                                    : laytimeData.dischargingRate
                            ),
                        }
                        : {}),
                },
            }));
        },
        [setState, laytimeData]
    );

    const updateEventTable = useCallback(
        (updatedEventTable) => {
            const calculatedRows = calculateTimeRows(updatedEventTable);
            setState((prevState) => ({
                ...prevState,
                data: {
                    ...prevState.data,
                    eventTable: calculatedRows,
                },
            }));
        },
        [setState]
    );

    const handleInputChange = useCallback(
        (id, field, value) => {
            const updatedEventTable =
                laytimeData.eventTable?.map((row) =>
                    row.id === id ? { ...row, [field]: value } : row
                ) || [];

            updateEventTable(updatedEventTable);
        },
        [laytimeData.eventTable, updateEventTable]
    );

    const handleDateChange = useCallback(
        (id, field, date, index) => {
            const laytimeStart = laytimeData?.laytimeStarts;
            const departureTime = laytimeData?.departureTime;

            if (!laytimeStart) {
                toastError("Please set both laytime start and end times");
                return;
            }

            const toMoment = (time) => (time ? moment(time) : null);
            const newTime = toMoment(date);
            const laytimeStarts = toMoment(laytimeStart);
            const departureTimes = toMoment(departureTime);
            const eventTable = laytimeData.eventTable || [];

            let updatedEventTable = [...eventTable];
            let currRow = { ...updatedEventTable[index] };
            const nextRow = { ...updatedEventTable[index + 1] };
            const fromTime = toMoment(currRow.fromTime);

            if (field === "fromTime" && newTime) {
                if (laytimeStarts && newTime.isBefore(laytimeStarts)) {
                    toastError("From time cannot be before laytime start time");
                    return;
                }
                if (departureTimes && newTime.isAfter(departureTimes)) {
                    toastError("From time cannot be after departure time");
                    return;
                }
            }

            if (field === "toTime" && newTime) {
                if (laytimeStarts && newTime.isBefore(laytimeStarts)) {
                    toastError("To time cannot be before laytime start time");
                    return;
                }
                if (currRow.toTime && newTime.isAfter(nextRow.toTime)) {
                    toastError("To time cannot be after next row's to time");
                    return;
                }
                if (departureTimes && newTime.isAfter(departureTimes)) {
                    toastError("To time cannot be after departure time");
                    return;
                }
                if (currRow.fromTime && newTime.isBefore(fromTime)) {
                    toastError("To time cannot be before from time");
                    return;
                }
            }

            currRow[field] = date;
            updatedEventTable[index] = currRow;

            if (field === "toTime" && date && index < updatedEventTable.length - 1) {
                let nextRow = { ...updatedEventTable[index + 1], fromTime: date };
                updatedEventTable[index + 1] = nextRow;
            }

            updateEventTable(updatedEventTable);
        },
        [laytimeData, updateEventTable]
    );

    const addRow = useCallback(() => {
        const eventTable = laytimeData.eventTable || [];
        const newId =
            eventTable.length > 0
                ? Math.max(...eventTable.map((row) => row.id)) + 1
                : 1;
        const lastRow =
            eventTable.length > 0 ? eventTable[eventTable.length - 1] : null;

        const newRow = {
            id: newId,
            fromTime: lastRow?.toTime || null,
            fromDay: lastRow?.toDay || "",
            toTime: null,
            toDay: "",
            event: "",
            timeDuration: 0,
            percentTimeToCount: 100,
            timeUsed: 0,
            cumulatedTimeUsed: 0,
            comments: "",
        };

        const updatedEventTable = [...eventTable, newRow];
        updateEventTable(updatedEventTable);
    }, [laytimeData.eventTable, updateEventTable]);

    const deleteRow = useCallback(
        (id) => {
            const updatedEventTable =
                laytimeData.eventTable?.filter((row) => row.id !== id) || [];
            updateEventTable(updatedEventTable);
        },
        [laytimeData.eventTable, updateEventTable]
    );

    function getInputValue(doc, id) {
        const el = doc.getElementById(id);
        if (!el) return "";
        if (el.tagName.toUpperCase() === "INPUT" || el.tagName.toUpperCase() === "TEXTAREA") {
            return el.value || "";
        }
        return el.textContent || "";
    }

    function getDateValue(doc, id) {
        const el = doc.getElementById(id);
        if (!el) return null;

        const value = el.value || el.textContent || el.innerText || "";
        if (!value) return null;

        const date = new Date(value);
        return isNaN(date.getTime()) ? null : date;
    }

    const getNumberValue = (doc, id) => {
        const val = getInputValue(doc, id);
        if (!val || val.trim() === "") return null;

        const num = parseFloat(val);
        return isNaN(num) ? null : num;
    };

    function getSpanText(doc, id) {
        const el = doc.getElementById(id);
        if (!el) return "";
        return (el.textContent || el.innerText || "").trim();
    }

    const parseHtmlFile = async (file) => {
        try {
            const text = await file.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(text, "text/html");

            if (!doc || doc.querySelector('parsererror')) {
                throw new Error('Failed to parse HTML file');
            }

            const vesselData = {
                vesselName: getInputValue(doc, "vesselName"),
                cargoDesc: getInputValue(doc, "cargoDesc"),
                billOfLadingDate: getDateValue(doc, "billOfLadingDate"),
                voyageNo: getInputValue(doc, "voyageNo"),
                charterer: getInputValue(doc, "charterer"),
                laytimePort: getInputValue(doc, "laytimePort"),
                operation: getInputValue(doc, "operation"),
                shipmentId: getInputValue(doc, "shipmentId"),
                cargoQty: getNumberValue(doc, "cargoQty"),
                dischargingRate: getNumberValue(doc, "dischargingRate"),
                dischargingTerms: getInputValue(doc, "dischargingTerms"),
                turnTimeAllowed: getNumberValue(doc, "turnTimeAllowed"),
                turnTimeClause: getInputValue(doc, "turnTimeClause"),
                demurrageRate: getNumberValue(doc, "demurrageRate"),
                despatchRate: getNumberValue(doc, "despatchRate"),
                totalTimeAllowed: getNumberValue(doc, "totalTimeAllowed"),
                arrivalTime: getDateValue(doc, "arrivalTime"),
                norTendered: getDateValue(doc, "norTendered"),
                norTerms: getInputValue(doc, "norTerms"),
                laytimeStarts: getDateValue(doc, "laytimeStarts"),
                commencedDischarging: getDateValue(doc, "commencedDischarging"),
                completedDischarging: getDateValue(doc, "completedDischarging"),
                laytimeStops: getDateValue(doc, "laytimeStops"),
                vesselOnDemurrage: getDateValue(doc, "vesselOnDemurrage"),
                departureTime: getDateValue(doc, "departureTime"),
            };

            const eventTableBody = doc.querySelector("#eventTableBody");
            let eventTable = [];

            if (eventTableBody) {
                const eventRows = Array.from(eventTableBody.querySelectorAll("tr"));
                eventTable = eventRows.map((tr) => {
                    const tds = tr.querySelectorAll("td");
                    if (tds.length < 10) return null;

                    const getInputFromTd = (td, inputType = "input") => {
                        if (!td) return "";
                        const input = td.querySelector(inputType);
                        return input ? (input.value || "") : (td.textContent || td.innerText || "").trim();
                    };

                    const getTextFromTd = (td) => {
                        if (!td) return "";
                        return (td.textContent || td.innerText || "").trim();
                    };

                    return {
                        fromTime: getInputFromTd(tds[1]),
                        fromDay: getTextFromTd(tds[2]),
                        toTime: getInputFromTd(tds[3]),
                        toDay: getTextFromTd(tds[4]),
                        event: getInputFromTd(tds[5]),
                        timeDuration: getTextFromTd(tds[6]),
                        percentTimeToCount: getInputFromTd(tds[7]),
                        timeUsed: getTextFromTd(tds[8]),
                        cumulatedTimeUsed: getTextFromTd(tds[9]),
                        comments: tds[10] ? getInputFromTd(tds[10]) : "",
                    };
                }).filter(Boolean);
            }

            const summary = {
                totalDuration: getSpanText(doc, "totalDuration"),
                totalTimeUsed: getSpanText(doc, "totalTimeUsed"),
                totalTimeAllowedSummary: getSpanText(doc, "totalTimeAllowedSummary"),
                difference: getSpanText(doc, "difference"),
                demurrage: getSpanText(doc, "demurrage"),
            };

            return {
                ...vesselData,
                eventTable,
                summary,
            };

        } catch (error) {
            console.error('Error parsing HTML file:', error);
            throw new Error(`Failed to parse HTML file: ${error.message}`);
        }
    };

    const handleFile = async (e) => {
        const file = e.target.files[0];
        e.target.value = null;
        if (!file) return;

        try {
            const parsedData = await parseHtmlFile(file);

            setState((prev) => ({
                ...prev,
                data: {
                    ...prev.data,
                    ...parsedData
                }
            }));
        } catch (err) {
            toastError("Failed to parse file.");
        }
    };

    const renderDatePicker = useCallback(
        (field, label) => {
            const rawVal = laytimeData[field];
            const validVal = rawVal && moment(rawVal).isValid() ? moment(rawVal) : null;
            return (
                <tr className="hover:bg-[#ffffcc]/60">
                    <td className="w-1/2 border-r border-b border-slate-300 bg-slate-50 px-1.5 py-0 text-[11px] font-medium text-slate-800 align-middle truncate h-[19px]">
                        {label}
                    </td>
                    <td className="w-1/2 border-b border-slate-300 p-0 align-middle bg-[#ffffd6] h-[19px]">
                        <MuiPickersUtilsProvider utils={MomentUtils}>
                            <Tooltip
                                title={
                                    <span style={{ fontSize: "0.73rem" }}>
                                        {validVal
                                            ? `Selected: ${validVal.format("DD/MM/YYYY HH:mm")}`
                                            : "Select date and time"}
                                    </span>
                                }
                                placement="top"
                                arrow
                            >
                                <KeyboardDateTimePicker
                                    size="small"
                                    variant="dialog"
                                    ampm={false}
                                    margin="none"
                                    style={{ width: "100%", height: "19px", margin: 0, padding: 0 }}
                                    className="laytime-datepicker w-full bg-transparent h-[19px] text-[11px] text-slate-900 !m-0 !p-0 [&_.MuiFormControl-root]:!m-0 [&_.MuiFormControl-root]:!p-0 [&_.MuiFormControl-root]:!h-[19px] [&_.MuiFormControl-root]:!flex-row [&_.MuiFormControl-root]:!items-center [&_.MuiInputBase-root]:!h-[19px] [&_.MuiInputBase-root]:!flex-row [&_.MuiInputBase-root]:!items-center [&_.MuiInputBase-root]:!justify-start [&_.MuiInputBase-root]:!w-full [&_.MuiInputBase-root]:!min-h-0 [&_.MuiInputBase-root]:!bg-transparent [&_.MuiInputBase-root]:!bg-none [&_.MuiInputBase-input]:!h-[19px] [&_.MuiInputBase-input]:!py-0 [&_.MuiInputBase-input]:!px-1.5 [&_.MuiInputBase-input]:!w-auto [&_.MuiInputBase-input]:!flex-initial [&_.MuiInputBase-input]:!text-[11px] [&_.MuiInputBase-input]:!leading-normal [&_.MuiInputBase-input]:!bg-transparent [&_input]:!bg-transparent [&_input[readonly]]:!bg-transparent [&_input::placeholder]:!text-[9.5px] [&_input::placeholder]:!text-slate-400 [&_.MuiInput-underline]:before:!border-none [&_.MuiInput-underline]:after:!border-none [&_.MuiInputAdornment-root]:!ml-1 [&_.MuiInputAdornment-root]:!h-[19px] [&_.MuiIconButton-root]:!p-0 [&_.MuiIconButton-root]:!h-[19px] [&_.MuiIconButton-root]:!w-[19px] [&_.MuiSvgIcon-root]:!w-3 [&_.MuiSvgIcon-root]:!h-3 [&_.MuiSvgIcon-root]:!text-[10px] [&_.MuiInputBase-root.Mui-focused]:ring-1 [&_.MuiInputBase-root.Mui-focused]:ring-inset [&_.MuiInputBase-root.Mui-focused]:ring-amber-600 [&_.MuiInputBase-root.Mui-focused]:bg-white"
                                    format="DD/MM/YYYY HH:mm"
                                    value={validVal}
                                    onChange={(date) => updateLayTimeData(field, date)}
                                    placeholder="DD/MM/YYYY HH:mm"
                                    inputProps={{
                                        style: {
                                            backgroundColor: "transparent",
                                            height: "19px",
                                            minHeight: "19px",
                                            maxHeight: "19px",
                                            padding: "0 4px",
                                            fontSize: "11px",
                                            lineHeight: "normal",
                                        },
                                    }}
                                    InputProps={{
                                        readOnly: true,
                                        disableUnderline: true,
                                        style: {
                                            fontSize: "11px",
                                            padding: "0 2px",
                                            height: "19px",
                                            minHeight: "19px",
                                            maxHeight: "19px",
                                            backgroundColor: "transparent",
                                        },
                                    }}
                                    error={false}
                                    helperText={null}
                                    FormHelperTextProps={{ style: { display: "none" } }}
                                    onError={() => { }}
                                    keyboardIcon={<CalendarTodayIcon color="action" style={{ fontSize: "0.55rem" }} />}
                                    KeyboardButtonProps={{ style: { padding: 0, margin: 0 } }}
                                />
                            </Tooltip>
                        </MuiPickersUtilsProvider>
                    </td>
                </tr>
            );
        },
        [laytimeData, updateLayTimeData]
    );

    const renderTabelDatePicker = useCallback(
        (id, field, value, index) => {
            const validVal = value && moment(value).isValid() ? moment(value) : null;
            return (
                <MuiPickersUtilsProvider utils={MomentUtils}>
                    <KeyboardDateTimePicker
                        value={validVal}
                        onChange={(date) => handleDateChange(id, field, date, index)}
                        format="DD/MM/YYYY HH:mm"
                        placeholder="DD/MM/YYYY HH:mm"
                        ampm={false}
                        margin="none"
                        style={{ width: "100%", height: "19px", margin: 0, padding: 0 }}
                        className="laytime-datepicker w-full bg-transparent h-[19px] text-[11px] text-slate-900 !m-0 !p-0 [&_.MuiFormControl-root]:!m-0 [&_.MuiFormControl-root]:!p-0 [&_.MuiFormControl-root]:!h-[19px] [&_.MuiFormControl-root]:!flex-row [&_.MuiFormControl-root]:!items-center [&_.MuiInputBase-root]:!h-[19px] [&_.MuiInputBase-root]:!flex-row [&_.MuiInputBase-root]:!items-center [&_.MuiInputBase-root]:!justify-start [&_.MuiInputBase-root]:!w-full [&_.MuiInputBase-root]:!min-h-0 [&_.MuiInputBase-root]:!bg-transparent [&_.MuiInputBase-root]:!bg-none [&_.MuiInputBase-input]:!h-[19px] [&_.MuiInputBase-input]:!py-0 [&_.MuiInputBase-input]:!px-1.5 [&_.MuiInputBase-input]:!w-auto [&_.MuiInputBase-input]:!flex-initial [&_.MuiInputBase-input]:!text-[11px] [&_.MuiInputBase-input]:!leading-[19px] [&_.MuiInputBase-input]:!bg-transparent [&_input]:!bg-transparent [&_input[readonly]]:!bg-transparent [&_input::placeholder]:!text-[9.5px] [&_input::placeholder]:!text-slate-400 [&_.MuiInput-underline]:before:!border-none [&_.MuiInput-underline]:after:!border-none [&_.MuiInputAdornment-root]:!ml-1 [&_.MuiInputAdornment-root]:!h-[19px] [&_.MuiIconButton-root]:!p-0 [&_.MuiIconButton-root]:!h-[19px] [&_.MuiIconButton-root]:!w-[19px] [&_.MuiSvgIcon-root]:!w-3 [&_.MuiSvgIcon-root]:!h-3 [&_.MuiSvgIcon-root]:!text-[10px] [&_.MuiInputBase-root.Mui-focused]:ring-1 [&_.MuiInputBase-root.Mui-focused]:ring-inset [&_.MuiInputBase-root.Mui-focused]:ring-amber-600 [&_.MuiInputBase-root.Mui-focused]:bg-white"
                        inputProps={{
                            style: {
                                backgroundColor: "transparent",
                                height: "19px",
                                minHeight: "19px",
                                maxHeight: "19px",
                                padding: "0 4px",
                                fontSize: "11px",
                                lineHeight: "19px",
                            },
                        }}
                        InputProps={{
                            readOnly: true,
                            disableUnderline: true,
                            style: {
                                fontSize: "11px",
                                padding: "0 2px",
                                height: "19px",
                                minHeight: "19px",
                                maxHeight: "19px",
                                backgroundColor: "transparent",
                            },
                        }}
                        error={false}
                        helperText={null}
                        FormHelperTextProps={{
                            style: { display: "none" },
                        }}
                        onError={() => { }}
                        keyboardIcon={
                            <CalendarTodayIcon color="action" style={{ fontSize: "0.55rem" }} />
                        }
                        KeyboardButtonProps={{ style: { padding: 0, margin: 0 } }}
                    />
                </MuiPickersUtilsProvider>
            );
        },
        [handleDateChange]
    );

    const renderInput = useCallback(
        (field, label, type = "text") => (
            <tr className="hover:bg-[#ffffcc]/60">
                <td className="w-1/2 border-r border-b border-slate-300 bg-slate-50 px-1.5 py-0 text-[11px] font-medium text-slate-800 align-middle truncate h-[19px]">
                    {label}
                </td>
                <td className="w-1/2 border-b border-slate-300 p-0 align-middle bg-[#ffffd6] h-[19px]">
                    <input
                        type={type}
                        value={laytimeData[field] || ""}
                        onChange={(e) => updateLayTimeData(field, e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        className="w-full h-[19px] text-[11px] px-1.5 py-0 block outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 bg-[#ffffd6] text-slate-900 border-none font-medium"
                    />
                </td>
            </tr>
        ),
        [laytimeData, updateLayTimeData]
    );

    const renderReadOnlyInput = useCallback(
        (field, label) => {
            const rawVal = laytimeData[field];
            const displayVal =
                field === "vesselOnDemurrage"
                    ? rawVal && moment(rawVal).isValid()
                        ? moment(rawVal).format("DD/MM/YYYY HH:mm")
                        : "-"
                    : rawVal || "";
            return (
                <tr className="hover:bg-[#ffffcc]/60">
                    <td className="w-1/2 border-r border-b border-slate-300 bg-slate-50 px-1.5 py-0 text-[11px] font-medium text-slate-800 align-middle truncate h-[19px]">
                        {label}
                    </td>
                    <td className="w-1/2 border-b border-slate-300 p-0 align-middle bg-slate-100 h-[19px]">
                        <input
                            type="text"
                            value={displayVal}
                            readOnly
                            className="w-full h-[19px] text-[11px] px-1.5 py-0 block outline-none bg-slate-100 text-slate-700 border-none font-medium cursor-not-allowed"
                        />
                    </td>
                </tr>
            );
        },
        [laytimeData]
    );

    return (
        <div className="space-y-3 font-sans">
            <div className="flex items-center justify-end gap-1.5 mb-2">
                <input
                    ref={inputFileRef}
                    type="file"
                    accept=".html,.htm"
                    style={{ display: "none" }}
                    onChange={(event) => { setOpenUploadHTML(false); handleFile(event); }}
                />
                <button
                    onClick={onButtonClick}
                    className="flex items-center gap-1.5 px-2.5 py-0.5 h-[22px] text-[11px] font-medium text-white bg-[#313C4C] border border-[#313C4C] rounded-none cursor-pointer hover:bg-[#0c2f50] transition-colors"
                >
                    <BsFiletypeHtml size={12} />
                    <span>Upload HTML</span>
                </button>
                <DownloadPdf
                    pdfData={{ ...laytimeData, ...laytimeSummary }}
                    isLaytimeStatement={true}
                    onDownloadElectricPdf={onDownloadElectricPdf}
                />
            </div>
            {laytimeData.vesselName && (
                <div className="text-center mb-2">
                    <h4 className="font-medium text-[13px] uppercase text-[#313C4C] m-0 underline underline-offset-2 decoration-slate-600">
                        Laytime Statement for{" "}
                        <span className="text-slate-900 font-semibold">{laytimeData.vesselName}</span>{" "}
                        B/L Dated{" "}
                        <span className="text-slate-900 font-semibold">
                            {laytimeData.billOfLadingDate && moment(laytimeData.billOfLadingDate).isValid()
                                ? moment(laytimeData.billOfLadingDate).format("DD-MMM-YYYY")
                                : "N/A"}
                        </span>
                        : {laytimeData.shipmentId || "N/A"} For{" "}
                        <span className="text-slate-900 font-semibold">
                            {laytimeData.laytimePort || "N/A"}
                        </span>{" "}
                        - {laytimeData.operation || "N/A"}
                    </h4>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
                {/* First Column - Vessel Info */}
                <div className="border border-slate-300 bg-white overflow-hidden shadow-2xs">
                    <div className="bg-[#f0f2f5] border-b border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-800 text-center">
                        Vessel Info
                    </div>
                    <table className="w-full text-left border-collapse table-fixed [&_tr:last-child_td]:border-b-0">
                        <tbody>
                            {renderInput("vesselName", "Vessel Name", "text")}
                            {renderInput("cargoDesc", "Cargo Desc", "text")}
                            {renderDatePicker("billOfLadingDate", "B/L Date")}
                            {renderInput("voyageNo", "Voyage No", "text")}
                            {renderInput("charterer", "Charterer", "text")}
                            {renderInput("laytimePort", "Laytime for Port", "text")}
                            {renderInput("operation", "Operation", "text")}
                            {renderInput("shipmentId", "Shipment ID", "text", true)}
                        </tbody>
                    </table>
                </div>

                {/* Second Column - Terms & Rates */}
                <div className="border border-slate-300 bg-white overflow-hidden shadow-2xs">
                    <div className="bg-[#f0f2f5] border-b border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-800 text-center">
                        Terms & Rates
                    </div>
                    <table className="w-full text-left border-collapse table-fixed [&_tr:last-child_td]:border-b-0">
                        <tbody>
                            {renderInput("cargoQty", "Cargo Quantity (MT)", "number")}
                            {renderInput("dischargingRate", "Discharging Rate", "number")}
                            {renderInput("dischargingTerms", "Discharging Terms", "text")}
                            {renderInput("turnTimeAllowed", "Turn Time Allowed", "number")}
                            {renderInput("turnTimeClause", "Turn Time Clause", "text")}
                            {renderInput("demurrageRate", "Demurrage Rate ($/day)", "number")}
                            {renderInput("despatchRate", "Despatch Rate ($/day)", "number")}
                            {renderReadOnlyInput("totalTimeAllowed", "Total Time Allowed", true)}
                        </tbody>
                    </table>
                </div>

                {/* Third Column - Time Marks */}
                <div className="border border-slate-300 bg-white overflow-hidden shadow-2xs">
                    <div className="bg-[#f0f2f5] border-b border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-800 text-center">
                        Time Marks
                    </div>
                    <table className="w-full text-left border-collapse table-fixed [&_tr:last-child_td]:border-b-0">
                        <tbody>
                            {renderDatePicker("arrivalTime", "Arrival time")}
                            {renderDatePicker("norTendered", "NOR Tendered")}
                            {renderInput("norTerms", "NOR Terms", "text")}
                            {renderDatePicker("laytimeStarts", "Laytime Starts")}
                            {renderDatePicker("commencedDischarging", "Commenced Discharging")}
                            {renderDatePicker("completedDischarging", "Completed Discharging")}
                            {renderDatePicker("laytimeStops", "Laytime Stops")}
                            {renderReadOnlyInput("vesselOnDemurrage", "Vessel on Demurrage")}
                            {renderDatePicker("departureTime", "Departure Time", true)}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Events Table Section */}
            <div className="border border-slate-300 bg-white overflow-hidden shadow-2xs mb-3">
                <div className="bg-[#f0f2f5] border-b border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-800">
                    Events Table
                </div>
                <div className="overflow-x-auto scroll-thin w-full">
                    <table className="w-full text-left border-collapse table-fixed min-w-[1050px] [&_tr:last-child_td]:border-b-0">
                        <thead>
                            <tr>
                                <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[32px]">
                                    Add
                                </th>
                                <th className="px-1 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[115px]">
                                    From Time
                                </th>
                                <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[35px]">
                                    Day
                                </th>
                                <th className="px-1 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[125px]">
                                    To Time
                                </th>
                                <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[35px]">
                                    Day
                                </th>
                                <th className="px-1.5 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle min-w-[160px]">
                                    Event
                                </th>
                                <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle whitespace-normal leading-tight w-[95px]">
                                    Duration (days)
                                </th>
                                <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle whitespace-normal leading-tight w-[95px]">
                                    % Time to Count
                                </th>
                                <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle whitespace-normal leading-tight w-[95px]">
                                    Time Used (days)
                                </th>
                                <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle whitespace-normal leading-tight w-[95px]">
                                    Cumulated Time
                                </th>
                                <th className="px-1.5 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle min-w-[130px]">
                                    Comments
                                </th>
                                <th className="px-1 py-0.5 text-center border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[52px]">
                                    Actions
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {laytimeData.eventTable?.length > 0 ? (
                                laytimeData.eventTable.map((row, index) => {
                                    const isAfterDeparture =
                                        (row.fromTime &&
                                            laytimeData.vesselOnDemurrage &&
                                            new Date(row.fromTime) >
                                            new Date(laytimeData.vesselOnDemurrage)) ||
                                        (row.toTime &&
                                            laytimeData.vesselOnDemurrage &&
                                            new Date(row.toTime) >
                                            new Date(laytimeData.vesselOnDemurrage));
                                    return (
                                        <tr key={row.id} className="hover:bg-[#ffffcc]/60">
                                            <td className="border-r border-b border-slate-300 p-0 text-center bg-slate-50 align-middle">
                                                <div className="flex items-center justify-center h-[19px]">
                                                    <button
                                                        onClick={addRow}
                                                        className="p-0 text-blue-600 hover:text-blue-800 transition cursor-pointer"
                                                        title="Add row"
                                                    >
                                                        <BiPlus style={{ width: "9px", height: "9px", minWidth: "9px", minHeight: "9px" }} />
                                                    </button>
                                                </div>
                                            </td>
                                            <td className={`border-r border-b border-slate-300 p-0 align-middle ${isAfterDeparture ? "bg-rose-50 text-rose-700 font-semibold" : "bg-slate-50"}`}>
                                                <div className="h-[19px] px-1.5 flex items-center text-[11px] font-medium text-slate-800 whitespace-nowrap">
                                                    {row.fromTime && moment(row.fromTime).isValid()
                                                        ? moment(row.fromTime).format("DD/MM/YYYY HH:mm")
                                                        : "-"}
                                                </div>
                                            </td>
                                            <td className="border-r border-b border-slate-300 p-0 text-center bg-slate-50 align-middle">
                                                <div className="h-[19px] flex items-center justify-center text-[11px] font-medium text-slate-600 uppercase">
                                                    {row.fromDay || "-"}
                                                </div>
                                            </td>
                                            <td className={`border-r border-b border-slate-300 p-0 align-middle ${isAfterDeparture ? "bg-rose-50" : "bg-[#ffffd6]"}`}>
                                                {renderTabelDatePicker(row.id, "toTime", row.toTime, index)}
                                            </td>
                                            <td className="border-r border-b border-slate-300 p-0 text-center bg-slate-50 align-middle">
                                                <div className="h-[19px] flex items-center justify-center text-[11px] font-medium text-slate-600 uppercase">
                                                    {row.toDay || "-"}
                                                </div>
                                            </td>
                                            <td className="border-r border-b border-slate-300 p-0 align-middle bg-[#ffffd6]">
                                                <input
                                                    value={row.event || ""}
                                                    onChange={(e) =>
                                                        handleInputChange(row.id, "event", e.target.value)
                                                    }
                                                    className="w-full h-[19px] text-[11px] px-1.5 py-0 block outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 bg-[#ffffd6] text-slate-900 border-none font-medium placeholder:text-gray-400"
                                                    placeholder="Enter event..."
                                                />
                                            </td>
                                            <td className="border-r border-b border-slate-300 p-0 align-middle bg-slate-100 text-center">
                                                <div className="h-[19px] flex items-center justify-center text-[11px] font-medium text-slate-800">
                                                    {row.timeDuration !== undefined && row.timeDuration !== null ? row.timeDuration : "0"}
                                                </div>
                                            </td>
                                            <td className="border-r border-b border-slate-300 p-0 align-middle bg-[#ffffd6] text-center">
                                                <input
                                                    type="number"
                                                    value={row.percentTimeToCount !== undefined ? row.percentTimeToCount : ""}
                                                    onWheel={(e) => e.target.blur()}
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            row.id,
                                                            "percentTimeToCount",
                                                            e.target.value
                                                        )
                                                    }
                                                    min={0}
                                                    max={100}
                                                    className="w-full h-[19px] text-[11px] px-1.5 py-0 text-center block outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 bg-[#ffffd6] text-slate-900 border-none font-medium"
                                                    placeholder="0"
                                                />
                                            </td>
                                            <td className="border-r border-b border-slate-300 p-0 align-middle bg-slate-100 text-center">
                                                <div className="h-[19px] flex items-center justify-center text-[11px] font-medium text-slate-800">
                                                    {row.timeUsed !== undefined && row.timeUsed !== null ? row.timeUsed : "0"}
                                                </div>
                                            </td>
                                            <td className="border-r border-b border-slate-300 p-0 align-middle bg-slate-100 text-center">
                                                <div className="h-[19px] flex items-center justify-center text-[11px] font-medium text-slate-800">
                                                    {row.cumulatedTimeUsed !== undefined && row.cumulatedTimeUsed !== null ? row.cumulatedTimeUsed : "0"}
                                                </div>
                                            </td>
                                            <td className="border-r border-b border-slate-300 p-0 align-middle bg-[#ffffd6]">
                                                <input
                                                    value={row.comments || ""}
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            row.id,
                                                            "comments",
                                                            e.target.value
                                                        )
                                                    }
                                                    className="w-full h-[19px] text-[11px] px-1.5 py-0 block outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 bg-[#ffffd6] text-slate-900 border-none font-medium placeholder:text-gray-400"
                                                    placeholder="Comments..."
                                                />
                                            </td>
                                            <td className="border-b border-slate-300 p-0 text-center bg-slate-50 align-middle">
                                                <div className="flex items-center justify-center h-[19px]">
                                                    <button
                                                        onClick={() => deleteRow(row.id)}
                                                        className="p-0 text-rose-600 hover:text-rose-800 transition cursor-pointer"
                                                        title="Delete row"
                                                    >
                                                        <FiTrash2 className="laytime-delete-icon" style={{ width: "11px", height: "11px", minWidth: "11px", minHeight: "11px" }} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td
                                        colSpan={12}
                                        className="px-4 py-2 text-center text-slate-400 italic text-[11px]"
                                    >
                                        <div className="flex flex-col items-center justify-center gap-1">
                                            <span>No event logs added yet.</span>
                                            <button
                                                onClick={addRow}
                                                className="px-2.5 py-0.5 text-[11px] font-medium bg-[#313C4C] text-white hover:bg-[#0d508f] transition cursor-pointer flex items-center gap-1"
                                            >
                                                <BiPlus size={14} />
                                                <span>Add First Row</span>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Laytime Summary Card */}
            <div className="mt-1 flex w-full justify-end">
                <div className="border border-slate-300 bg-white overflow-hidden shadow-2xs w-full md:w-80">
                    <div className="bg-[#f0f2f5] border-b border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-800 text-center">
                        Laytime Summary
                    </div>
                    <table className="w-full text-left border-collapse table-fixed [&_tr:last-child_td]:border-b-0">
                        <tbody>
                            <tr className="hover:bg-[#ffffcc]/60">
                                <td className="w-1/2 border-r border-b border-slate-300 bg-slate-50 px-2 py-0 text-[11px] font-medium text-slate-800 align-middle h-[19px]">
                                    Total Duration:
                                </td>
                                <td className="w-1/2 border-b border-slate-300 px-2 py-0 text-[11px] font-medium text-slate-900 text-right bg-[#ffffd6] align-middle h-[19px]">
                                    {laytimeSummary?.totalDuration?.toFixed(5) || 0} days
                                </td>
                            </tr>
                            <tr className="hover:bg-[#ffffcc]/60">
                                <td className="w-1/2 border-r border-b border-slate-300 bg-slate-50 px-2 py-0 text-[11px] font-medium text-slate-800 align-middle h-[19px]">
                                    Total Time Used:
                                </td>
                                <td className="w-1/2 border-b border-slate-300 px-2 py-0 text-[11px] font-medium text-slate-900 text-right bg-[#ffffd6] align-middle h-[19px]">
                                    {laytimeSummary?.totalTimeUsed?.toFixed(5) || 0} days
                                </td>
                            </tr>
                            <tr className="hover:bg-[#ffffcc]/60">
                                <td className="w-1/2 border-r border-b border-slate-300 bg-slate-50 px-2 py-0 text-[11px] font-medium text-slate-800 align-middle h-[19px]">
                                    Total Time Allowed:
                                </td>
                                <td className="w-1/2 border-b border-slate-300 px-2 py-0 text-[11px] font-medium text-slate-900 text-right bg-[#ffffd6] align-middle h-[19px]">
                                    {laytimeSummary?.totalTimeAllowed?.toFixed(5) || 0} days
                                </td>
                            </tr>
                            <tr className="hover:bg-[#ffffcc]/60">
                                <td className="w-1/2 border-r border-b border-slate-300 bg-slate-50 px-2 py-0 text-[11px] font-medium text-slate-900 align-middle h-[19px]">
                                    Difference:
                                </td>
                                <td className={`w-1/2 border-b border-slate-300 px-2 py-0 text-[11px] font-medium text-right bg-[#ffffd6] align-middle h-[19px] ${laytimeSummary?.difference > 0 ? "text-emerald-700" : "text-rose-700"
                                    }`}>
                                    {laytimeSummary?.difference?.toFixed(5) || 0} days
                                </td>
                            </tr>
                            <tr className="hover:bg-[#ffffcc]/60">
                                <td className="w-1/2 border-r border-b border-slate-300 bg-slate-50 px-2 py-0 text-[11px] font-medium text-slate-900 align-middle h-[19px]">
                                    Demurrage / Despatch:
                                </td>
                                <td className={`w-1/2 border-b border-slate-300 px-2 py-0 text-[11px] font-medium text-right bg-[#ffffd6] align-middle h-[19px] ${laytimeSummary?.difference > 0 ? "text-emerald-700" : "text-rose-700"
                                    }`}>
                                    {laytimeSummary?.demurrage?.toFixed(3) || 0}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <HtmlImportGuide open={openUploadHTML} onClose={() => setOpenUploadHTML(false)} onChooseFile={() => inputFileRef.current?.click()} kind="laytime" />
        </div>
    );
};

export default LaytimeModel;
